// i18n engine (CONTENT-003). Vanilla — no framework. The active locale is read
// from the ?lang query (so every locale has a distinct, shareable URL that
// hreflang can point at), then a remembered choice, then the browser, then EN.

import { CATALOG, LOCALES, type Locale } from './catalog';

const STORAGE_KEY = 'asmaluz.locale';

function normalize(value: string | null | undefined): Locale | null {
  if (!value) return null;
  const prefix = value.toLowerCase().slice(0, 2);
  return (LOCALES as readonly string[]).includes(prefix) ? (prefix as Locale) : null;
}

function read(): Locale {
  const fromQuery = typeof location !== 'undefined' ? new URLSearchParams(location.search).get('lang') : null;
  let stored: string | null = null;
  try {
    stored = typeof localStorage !== 'undefined' ? localStorage.getItem(STORAGE_KEY) : null;
  } catch {
    stored = null;
  }
  const fromNav = typeof navigator !== 'undefined' ? navigator.language : null;
  return normalize(fromQuery) ?? normalize(stored) ?? normalize(fromNav) ?? 'en';
}

let current: Locale = read();

export function getLocale(): Locale {
  return current;
}

// setLocale persists the choice and reflects it in the URL (?lang=) so the page
// is shareable in that language. Does not re-render — the caller does.
export function setLocale(next: Locale): void {
  current = next;
  try {
    localStorage.setItem(STORAGE_KEY, next);
  } catch {
    /* storage may be unavailable (private mode) — the ?lang query still carries it */
  }
  try {
    const url = new URL(location.href);
    url.searchParams.set('lang', next);
    history.replaceState(history.state, '', url);
  } catch {
    /* non-browser context */
  }
}

// t resolves a catalog key in the active locale, falling back to EN, then the
// key itself (so a missing string is visible in dev, never blank).
export function t(key: string): string {
  return CATALOG[current][key] ?? CATALOG.en[key] ?? key;
}

export interface HeadMeta {
  titleKey: string;
  descKey: string;
}

// applyHead sets <html lang>, the per-locale <title> + description/OG, and the
// hreflang alternates for the current path.
export function applyHead(meta: HeadMeta): void {
  if (typeof document === 'undefined') return;
  document.documentElement.lang = current;
  document.title = t(meta.titleKey);
  setMeta('name', 'description', t(meta.descKey));
  setMeta('property', 'og:title', t(meta.titleKey));
  setMeta('property', 'og:description', t(meta.descKey));
  setMeta('property', 'og:locale', current);
  setHreflang();
}

function setMeta(attr: 'name' | 'property', key: string, content: string): void {
  let node = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  if (!node) {
    node = document.createElement('meta');
    node.setAttribute(attr, key);
    document.head.appendChild(node);
  }
  node.content = content;
}

function setHreflang(): void {
  document.head.querySelectorAll('link[rel="alternate"][data-i18n]').forEach((n) => n.remove());
  const base = `${location.origin}${location.pathname}`;
  const alternates: [string, Locale | 'x-default'][] = [
    ...LOCALES.map((l): [string, Locale] => [l, l]),
    ['en', 'x-default'],
  ];
  for (const [lang, hreflang] of alternates) {
    const link = document.createElement('link');
    link.rel = 'alternate';
    link.hreflang = hreflang;
    link.href = `${base}?lang=${lang}`;
    link.setAttribute('data-i18n', '');
    document.head.appendChild(link);
  }
}
